import { c as useScreenContext, r as FullScreen, s as KiddProvider } from "../store-key-fUtc4MNq.js";
import { a as Tabs, c as Select, f as Confirm, i as useSize, n as Output, s as TextInput, t as useHotkey, u as MultiSelect, y as ScrollArea } from "../use-key-binding-Cwei5N22.js";
import Module from "node:module";
import { relative, resolve } from "node:path";
import { toError } from "@kidd-cli/utils/fp";
import { watch } from "node:fs";
import { P as P$1, match as match$1 } from "ts-pattern";
import { hasTag, withTag } from "@kidd-cli/utils/tag";
import { glob } from "node:fs/promises";
import { Component, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import process$1 from "node:process";
import { Box, Spacer, Text, useApp, useInput } from "ink";
import { attempt as attempt$1, noop } from "es-toolkit";
//#region src/stories/story.ts
/**
* Define a single story for a component.
*
* Wraps the provided definition in a frozen, tagged object that the story
* registry and viewer can consume at runtime.
*
* @param def - The story definition including component, schema, and default props.
* @returns A frozen {@link Story} tagged with `'Story'`.
*/
function story(def) {
	const tagged = withTag({
		name: def.name,
		component: def.component,
		schema: def.schema,
		props: def.props,
		defaultKeys: [],
		decorators: Object.freeze(def.decorators ?? []),
		description: def.description
	}, "Story");
	return Object.freeze(tagged);
}
/**
* Define a group of related stories for a single component.
*
* Each variant shares the same component and schema but provides its own props
* and optional decorators. The group itself can also carry shared decorators.
*
* @param def - The group definition including title, component, schema, and variant map.
* @returns A frozen {@link StoryGroup} tagged with `'StoryGroup'`.
*/
function stories(def) {
	const groupDecorators = Object.freeze(def.decorators ?? []);
	const defaults = def.defaults ?? {};
	const defaultKeys = Object.keys(defaults);
	const resolvedStories = Object.freeze(Object.fromEntries(Object.entries(def.stories).map(([variantName, variant]) => [variantName, Object.freeze(withTag({
		name: variantName,
		component: def.component,
		schema: def.schema,
		props: {
			...defaults,
			...variant.props
		},
		defaultKeys,
		decorators: Object.freeze(variant.decorators ?? []),
		description: variant.description
	}, "Story"))])));
	const tagged = withTag({
		title: def.title,
		component: def.component,
		schema: def.schema,
		decorators: groupDecorators,
		stories: resolvedStories
	}, "StoryGroup");
	return Object.freeze(tagged);
}
//#endregion
//#region src/stories/decorators.tsx
/**
* Wrap a story component in a {@link KiddProvider} with the given screen context.
* Required for components that use `useScreenContext()`.
*
* @param ctx - The screen context to provide.
* @returns A decorator that wraps the story in a KiddProvider.
*/
function withContext(ctx) {
	return (StoryComponent) => {
		/**
		* @private
		*/
		function ContextWrapper(props) {
			return /* @__PURE__ */ jsx(KiddProvider, {
				value: ctx,
				children: /* @__PURE__ */ jsx(StoryComponent, { ...props })
			});
		}
		return ContextWrapper;
	};
}
/**
* Wrap a story component in a {@link FullScreen} alternate buffer.
*
* @returns A decorator that wraps the story in a FullScreen component.
*/
function withFullScreen() {
	return (StoryComponent) => {
		/**
		* @private
		*/
		function FullScreenWrapper(props) {
			return /* @__PURE__ */ jsx(FullScreen, { children: /* @__PURE__ */ jsx(StoryComponent, { ...props }) });
		}
		return FullScreenWrapper;
	};
}
/**
* Wrap a story component in a `Box` with fixed dimensions.
*
* @param options - Layout options including width and padding.
* @returns A decorator that wraps the story in a sized Box.
*/
function withLayout(options) {
	return (StoryComponent) => {
		/**
		* @private
		*/
		function LayoutWrapper(props) {
			return /* @__PURE__ */ jsx(Box, {
				width: options.width,
				padding: options.padding,
				children: /* @__PURE__ */ jsx(StoryComponent, { ...props })
			});
		}
		return LayoutWrapper;
	};
}
//#endregion
//#region src/stories/schema.ts
/**
* Introspect a Zod object schema into an array of field descriptors
* suitable for rendering prop editor controls.
*
* @param schema - The Zod object schema to introspect.
* @returns An array of frozen field descriptors, one per schema property.
*/
function schemaToFieldDescriptors(schema) {
	const shape = schema.shape;
	return Object.entries(shape).map(([name, fieldSchema]) => {
		const { inner, isOptional, defaultValue } = unwrapZodType(fieldSchema);
		const innerDef = inner._def;
		const typeName = innerDef.type ?? "unknown";
		const control = resolveControlKind({
			typeName,
			def: innerDef
		});
		const { description } = inner;
		return Object.freeze({
			name,
			control,
			isOptional,
			defaultValue,
			description,
			options: extractOptions(typeName, innerDef),
			zodTypeName: typeName
		});
	});
}
/**
* Map a Zod type name and definition to a field control kind.
*
* @param options - The type name and Zod definition to resolve.
* @returns The resolved control kind for the prop editor.
*/
function resolveControlKind({ typeName, def }) {
	return match$1(typeName).with("string", () => "text").with("number", () => "number").with("boolean", () => "boolean").with("enum", () => "select").with("nativeEnum", () => "select").with("literal", () => "readonly").with("array", () => resolveArrayControlKind(def)).with("object", () => "json").otherwise(() => "json");
}
/**
* Recursively unwrap optional and default wrappers from a Zod type.
*
* @private
* @param options - The recursive unwrap state.
* @returns The fully unwrapped type information.
*/
function unwrapZodTypeRecursive(options) {
	const { current, isOptional, defaultValue } = options;
	const def = current._def;
	return match$1(def.type).with("optional", () => {
		if (def.innerType) return unwrapZodTypeRecursive({
			current: def.innerType,
			defaultValue,
			isOptional: true
		});
		return {
			defaultValue,
			inner: current,
			isOptional: true
		};
	}).with("default", () => {
		const newDefault = resolveDefaultValue(def.defaultValue, defaultValue);
		if (def.innerType) return unwrapZodTypeRecursive({
			current: def.innerType,
			defaultValue: newDefault,
			isOptional: true
		});
		return {
			defaultValue: newDefault,
			inner: current,
			isOptional: true
		};
	}).otherwise(() => ({
		defaultValue,
		inner: current,
		isOptional
	}));
}
/**
* Unwrap a Zod schema to extract its base type, optionality, and default value.
*
* @private
* @param schema - The Zod type to unwrap.
* @returns The unwrapped type information.
*/
function unwrapZodType(schema) {
	return unwrapZodTypeRecursive({
		current: schema,
		defaultValue: void 0,
		isOptional: false
	});
}
/**
* Resolve a default value, preferring a defined value over the fallback.
*
* @private
* @param value - The candidate default value from the Zod definition.
* @param fallback - The fallback value when the candidate is undefined.
* @returns The resolved default value.
*/
function resolveDefaultValue(value, fallback) {
	if (value === void 0) return fallback;
	if (typeof value === "function") return value();
	return value;
}
/**
* Extract string options from enum-like Zod definitions.
*
* @private
* @param typeName - The Zod type name.
* @param def - The Zod definition to inspect.
* @returns An array of string options, or undefined for non-enum types.
*/
function extractOptions(typeName, def) {
	return match$1(typeName).with("enum", "nativeEnum", () => extractEnumEntries(def)).with("array", () => extractArrayElementOptions(def)).otherwise(() => void 0);
}
/**
* Extract enum entries from a Zod enum or nativeEnum definition.
*
* @private
* @param def - The Zod definition to inspect.
* @returns An array of string options, or undefined.
*/
function extractEnumEntries(def) {
	if (def.entries) return Object.values(def.entries).map(String);
}
/**
* Extract options from an array element type when the element is an enum.
*
* @private
* @param def - The Zod array definition to inspect.
* @returns An array of string options, or undefined.
*/
function extractArrayElementOptions(def) {
	if (def.element) {
		const elementDef = def.element._def;
		if (elementDef.type === "enum" || elementDef.type === "nativeEnum") return extractEnumEntries(elementDef);
	}
}
/**
* Determine the control kind for an array type based on its element type.
*
* @private
* @param def - The Zod array definition.
* @returns `'multiselect'` when the element type is an enum, otherwise `'json'`.
*/
function resolveArrayControlKind(def) {
	if (def.element) {
		const elementDef = def.element._def;
		if (elementDef.type === "enum" || elementDef.type === "nativeEnum") return "multiselect";
	}
	return "json";
}
//#endregion
//#region src/stories/validate.ts
/**
* Validate props against a Zod schema, returning per-field errors.
*
* @param options - The schema and props to validate.
* @returns An array of frozen field errors, empty when validation passes.
*/
function validateProps({ schema, props }) {
	const result = schema.safeParse(props);
	if (result.success) return [];
	return result.error.issues.map((issue) => Object.freeze({
		field: issue.path.map(String).join("."),
		message: issue.message
	}));
}
//#endregion
//#region src/stories/types.ts
/**
* File suffixes that identify story files (e.g. `.stories.tsx`).
* Shared between discovery and file watching.
*/
const STORY_FILE_SUFFIXES = [
	".stories.tsx",
	".stories.ts",
	".stories.jsx",
	".stories.js"
];
/**
* File extensions for source files that may affect story rendering.
* Used by the watcher to detect component changes.
*/
const SOURCE_FILE_EXTENSIONS = [
	".tsx",
	".ts",
	".jsx",
	".js"
];
//#endregion
//#region src/stories/discover.ts
/** Default include patterns for story file discovery. */
const DEFAULT_INCLUDE = STORY_FILE_SUFFIXES.map((suffix) => `src/**/*${suffix}`);
/**
* Default exclude patterns for story file discovery.
*/
const DEFAULT_EXCLUDE = ["node_modules/**"];
/**
* Discover story files matching the given patterns and import them.
*
* Uses Node 22+ `glob` from `node:fs/promises` for file matching. Each
* discovered file is imported via the provided {@link StoryImporter}.
*
* @param options - Discovery options including patterns and importer.
* @returns A {@link DiscoverResult} with entries map and any errors.
*/
async function discoverStories(options) {
	const patterns = options.include ?? DEFAULT_INCLUDE;
	const cwd = options.cwd ?? process.cwd();
	const filePaths = await collectFilePaths(patterns, cwd, options.exclude ?? DEFAULT_EXCLUDE);
	const results = await Promise.all(filePaths.map(async (filePath) => {
		const absolutePath = resolve(cwd, filePath);
		const [importError, entry] = await options.importer.importStory(absolutePath);
		if (importError) return { error: {
			filePath: absolutePath,
			message: importError.message
		} };
		return { entry: [absolutePath, entry] };
	}));
	const entries = new Map(results.filter(hasEntry).map((result) => result.entry));
	const errors = results.filter(hasError).map((result) => result.error);
	return Object.freeze({
		entries,
		errors: Object.freeze(errors)
	});
}
/**
* Collect file paths matching the given glob patterns.
*
* @private
* @param patterns - Glob patterns to match.
* @param cwd - Working directory for glob resolution.
* @param exclude - Patterns to exclude.
* @returns A flat array of matching file paths.
*/
async function collectFilePaths(patterns, cwd, exclude) {
	return (await Promise.all(patterns.map((pattern) => collectAsyncIterable(glob(pattern, {
		cwd,
		exclude: [...exclude]
	}))))).flat();
}
/**
* Drain an async iterable into an array.
*
* @private
* @param iterable - The async iterable to consume.
* @returns A promise resolving to an array of all yielded values.
*/
async function collectAsyncIterable(iterable) {
	const results = [];
	const iterator = iterable[Symbol.asyncIterator]();
	const drain = async () => {
		const next = await iterator.next();
		if (next.done) return results;
		results.push(next.value);
		return drain();
	};
	return drain();
}
/**
* Type guard for results that contain an entry.
*
* @private
* @param result - The result to check.
* @returns `true` when the result has an `entry` property.
*/
function hasEntry(result) {
	return "entry" in result;
}
/**
* Type guard for results that contain an error.
*
* @private
* @param result - The result to check.
* @returns `true` when the result has an `error` property.
*/
function hasError(result) {
	return "error" in result;
}
//#endregion
//#region src/stories/importer.ts
/**
* Create a story importer backed by jiti with cache disabled for hot reload.
*
* Installs a TypeScript extension resolution hook so that ESM-style `.js`
* imports (e.g. `from './alert.js'`) resolve to `.ts` / `.tsx` source files.
*
* Returns an error Result when the `jiti` peer dependency is not installed.
*
* @returns A Result with a frozen {@link StoryImporter} or an {@link Error}.
*/
function createStoryImporter() {
	const [jitiError, jitiCreateFn] = resolveJiti();
	if (jitiError) return [jitiError, null];
	try {
		installTsExtensionResolution();
		const jiti = jitiCreateFn(import.meta.url, {
			fsCache: false,
			moduleCache: false,
			interopDefault: true,
			jsx: { runtime: "automatic" }
		});
		return [null, Object.freeze({ importStory: async (filePath) => {
			try {
				const mod = await jiti.import(filePath);
				const entry = mod.default ?? mod;
				if (!isStoryEntry(entry)) return [/* @__PURE__ */ new Error(`File ${filePath} does not export a valid Story or StoryGroup`), null];
				return [null, entry];
			} catch (error) {
				return [toError(error), null];
			}
		} })];
	} catch (error) {
		return [toError(error), null];
	}
}
/**
* Attempt to resolve the `jiti` package at runtime.
*
* `jiti` is an optional peer dependency of `@kidd-cli/core`, but the stories
* subsystem cannot function without it. Returns a Result tuple so callers can
* surface a helpful message instead of crashing with a cryptic import error.
*
* @private
* @returns A Result with the `createJiti` factory or an {@link Error}.
*/
function resolveJiti() {
	try {
		return [null, Module.createRequire(import.meta.url)("jiti").createJiti];
	} catch (error) {
		if (error instanceof Error && error.code === "MODULE_NOT_FOUND" && (error.requireStack ?? []).length === 0) return [new Error([
			"The \"jiti\" package is required to run stories but was not found.",
			"",
			"Install it with:",
			"  pnpm add jiti"
		].join("\n")), null];
		return [toError(error), null];
	}
}
/**
* TypeScript extensions to try when a `.js` import fails to resolve.
*
* @private
*/
const TS_EXTENSIONS = [".ts", ".tsx"];
/**
* TypeScript extensions to try when a `.jsx` import fails to resolve.
*
* @private
*/
const TSX_EXTENSIONS = [
	".tsx",
	".ts",
	".jsx"
];
/**
* Name used to tag the patched `_resolveFilename` function so the
* patch can detect itself and remain idempotent.
*
* @private
*/
const PATCHED_FN_NAME = "__kidd_ts_resolve";
/**
* Patch `Module._resolveFilename` so that ESM-style `.js` / `.jsx` specifiers
* fall back to their TypeScript equivalents (`.ts`, `.tsx`).
*
* This is the same strategy used by `tsx` and `ts-node`. The patch is
* idempotent — calling it more than once is a no-op.
*
* @private
*/
function installTsExtensionResolution() {
	const mod = Module;
	const current = mod._resolveFilename;
	if (current.name === PATCHED_FN_NAME) return;
	const original = current;
	mod._resolveFilename = { [PATCHED_FN_NAME]: (request, parent, isMain, options) => {
		try {
			return original.call(mod, request, parent, isMain, options);
		} catch (error) {
			const resolved = resolveAlternateExtensions(request).reduce((found, alt) => {
				if (found) return found;
				try {
					return original.call(mod, alt, parent, isMain, options);
				} catch {
					return null;
				}
			}, null);
			if (resolved) return resolved;
			throw error;
		}
	} }[PATCHED_FN_NAME];
}
/**
* Build a list of alternate file paths to try when a `.js` or `.jsx`
* import fails to resolve.
*
* @private
* @param request - The original module specifier.
* @returns Alternate specifiers to try, or an empty array if not applicable.
*/
function resolveAlternateExtensions(request) {
	if (request.endsWith(".js")) {
		const base = request.slice(0, -3);
		return [...TS_EXTENSIONS.map((ext) => base + ext), base];
	}
	if (request.endsWith(".jsx")) {
		const base = request.slice(0, -4);
		return TSX_EXTENSIONS.map((ext) => base + ext);
	}
	return [];
}
/**
* Check whether a value is a valid {@link StoryEntry} (tagged as `Story` or `StoryGroup`).
*
* @private
* @param value - The value to check.
* @returns `true` when `value` carries a `Story` or `StoryGroup` tag.
*/
function isStoryEntry(value) {
	return hasTag(value, "Story") || hasTag(value, "StoryGroup");
}
//#endregion
//#region src/stories/registry.ts
/**
* Create a story registry with subscribe/notify for React integration.
*
* The registry maintains an internal mutable map and a separate immutable
* snapshot reference that is updated on every mutation. This allows
* `useSyncExternalStore` to detect changes via reference equality.
*
* @returns A frozen {@link StoryRegistry} instance.
*/
function createStoryRegistry() {
	const state = {
		entries: /* @__PURE__ */ new Map(),
		listeners: /* @__PURE__ */ new Set(),
		snapshot: /* @__PURE__ */ new Map()
	};
	return Object.freeze({
		getAll: () => state.snapshot,
		get: (name) => state.entries.get(name),
		set: (name, entry) => {
			if (state.entries.get(name) === entry) return;
			state.entries.set(name, entry);
			notify(state);
		},
		remove: (name) => {
			const deleted = state.entries.delete(name);
			if (deleted) notify(state);
			return deleted;
		},
		clear: () => {
			state.entries.clear();
			notify(state);
		},
		subscribe: (listener) => {
			state.listeners.add(listener);
			return () => {
				state.listeners.delete(listener);
			};
		},
		getSnapshot: () => state.snapshot
	});
}
/**
* Update the snapshot and notify all subscribers.
*
* @private
* @param state - The mutable registry state.
*/
function notify(state) {
	state.snapshot = Object.freeze(new Map(state.entries));
	[...state.listeners].map((listener) => {
		listener();
		return true;
	});
}
//#endregion
//#region src/stories/watcher.ts
/**
* Create a file watcher that monitors story directories for changes.
*
* Uses `node:fs.watch` with recursive mode. Debounces rapid FS events
* and re-imports changed story files via the importer.
*
* @param options - Watcher configuration.
* @returns A Result tuple containing either an error or a frozen {@link StoryWatcher}.
*/
function createStoryWatcher(options) {
	const debounceMs = options.debounceMs ?? 150;
	const timers = /* @__PURE__ */ new Map();
	const [watchError, watchers] = tryCreateWatchers(options.directories, debounceMs, timers, options);
	if (watchError) return [watchError, null];
	return [null, Object.freeze({ close: () => {
		watchers.map(closeWatcher);
		[...timers.values()].map(clearTimer);
		timers.clear();
	} })];
}
/**
* Attempt to create fs watchers for all directories. If any `watch()` call
* fails, close already-created watchers and clear timers, then return the error.
*
* @private
* @param directories - Directories to watch.
* @param debounceMs - Debounce interval in milliseconds.
* @param timers - The shared timer map.
* @param options - The watcher options containing importer and registry.
* @returns A Result tuple with either an error or the array of watchers.
*/
function tryCreateWatchers(directories, debounceMs, timers, options) {
	const result = directories.reduce((acc, dir) => {
		if (acc[0]) return acc;
		const [createError, watcher] = tryWatch(dir, debounceMs, timers, options);
		if (createError) {
			acc[1].map(closeWatcher);
			return [createError, null];
		}
		return [null, [...acc[1], watcher]];
	}, [null, []]);
	if (result[0]) {
		[...timers.values()].map(clearTimer);
		timers.clear();
	}
	return result;
}
/**
* Try to create a single fs watcher for a directory.
*
* @private
* @param dir - Directory to watch.
* @param debounceMs - Debounce interval in milliseconds.
* @param timers - The shared timer map.
* @param options - The watcher options containing importer and registry.
* @returns A Result tuple with either an error or the watcher.
*/
function tryWatch(dir, debounceMs, timers, options) {
	const [createError, watcher] = attempt$1(() => watch(dir, { recursive: true }, (_event, filename) => {
		if (filename === null || filename === void 0) return;
		if (isStoryFile(filename)) {
			const absolutePath = resolve(dir, filename);
			debouncedAction(absolutePath, debounceMs, timers, () => reloadStoryFile(absolutePath, options));
			return;
		}
		if (isSourceFile(filename)) debouncedAction(RELOAD_ALL_KEY, debounceMs, timers, () => reloadAllStories(options));
	}));
	if (createError) return [createError, null];
	watcher.on("error", noop);
	return [null, watcher];
}
/**
* Close an fs watcher and return it.
*
* @private
* @param watcher - The watcher to close.
* @returns The closed watcher.
*/
function closeWatcher(watcher) {
	watcher.close();
	return watcher;
}
/**
* Clear a timeout and return its id.
*
* @private
* @param timer - The timer to clear.
* @returns The cleared timer id.
*/
function clearTimer(timer) {
	clearTimeout(timer);
	return timer;
}
/**
* Check if a filename matches the story file convention.
*
* @private
* @param filename - The filename to check.
* @returns `true` when the filename ends with a story extension.
*/
function isStoryFile(filename) {
	return STORY_FILE_SUFFIXES.some((suffix) => filename.endsWith(suffix));
}
/**
* Check if a filename is a source file that could affect story rendering
* (e.g. a component file). Excludes files inside `node_modules`.
*
* @private
* @param filename - The filename to check.
* @returns `true` when the filename ends with a source extension.
*/
function isSourceFile(filename) {
	if (filename.includes("node_modules")) return false;
	return SOURCE_FILE_EXTENSIONS.some((ext) => filename.endsWith(ext));
}
/**
* Stable key used by {@link debouncedAction} so rapid source-file edits
* collapse into a single reload-all pass.
*
* @private
*/
const RELOAD_ALL_KEY = Symbol("reload_all");
/**
* Schedule a debounced action keyed by a string. Rapid calls with the same
* key collapse into a single invocation after `debounceMs`.
*
* @private
* @param key - Unique key for the debounce timer.
* @param debounceMs - Debounce interval in milliseconds.
* @param timers - The shared timer map.
* @param action - The async action to invoke after the debounce window.
*/
function debouncedAction(key, debounceMs, timers, action) {
	const existing = timers.get(key);
	if (existing !== void 0) clearTimeout(existing);
	const timer = setTimeout(() => {
		timers.delete(key);
		action().catch(() => void 0);
	}, debounceMs);
	timers.set(key, timer);
}
/**
* Invoke the optional reload lifecycle callbacks around an async action.
*
* @private
* @param options - The watcher options containing lifecycle callbacks.
* @param action - The async reload action to wrap.
*/
async function withReloadCallbacks(options, action) {
	if (options.onReloadStart !== void 0) options.onReloadStart();
	const callEnd = () => {
		if (options.onReloadEnd !== void 0) options.onReloadEnd();
		return true;
	};
	await action().then(callEnd, callEnd);
}
/**
* Import a single story entry and update the registry accordingly.
*
* @private
* @param filePath - Absolute path to the story file.
* @param options - The watcher options containing importer and registry.
*/
async function importAndUpdate(filePath, options) {
	const [importError, entry] = await options.importer.importStory(filePath);
	if (importError) options.registry.remove(filePath);
	else options.registry.set(filePath, entry);
}
/**
* Re-import a story file and update the registry.
*
* @private
* @param filePath - Absolute path to the story file.
* @param options - The watcher options containing importer and registry.
*/
async function reloadStoryFile(filePath, options) {
	await withReloadCallbacks(options, () => importAndUpdate(filePath, options));
}
/**
* Re-import all currently registered stories. Called when a non-story source
* file changes, since any story could depend on the changed component.
*
* @private
* @param options - The watcher options containing importer and registry.
*/
async function reloadAllStories(options) {
	await withReloadCallbacks(options, async () => {
		const storyKeys = [...options.registry.getAll().keys()];
		await Promise.all(storyKeys.map((filePath) => importAndUpdate(filePath, options)));
	});
}
//#endregion
//#region src/stories/check.ts
/** Maximum number of editable fields (schema keys minus default keys) per story. */
const MAX_EDITABLE_FIELDS = 6;
/**
* Validate all discovered story entries for common issues:
*
* - Too many editable fields (schema keys minus defaultKeys exceeds {@link MAX_EDITABLE_FIELDS})
* - Props validation errors against the schema
* - Missing required props
*
* @param entries - The discovered story entries to check.
* @returns A frozen {@link CheckResult} with all diagnostics.
*/
function checkStories(entries) {
	const diagnostics = [...entries.values()].flatMap(checkEntry);
	const storyCount = countStories(entries);
	return Object.freeze({
		diagnostics: Object.freeze(diagnostics),
		storyCount,
		passed: diagnostics.filter((d) => d.severity === "error").length === 0
	});
}
/**
* Check a single entry (story or group) and return diagnostics.
*
* @private
* @param entry - The story entry to check.
* @returns An array of diagnostics for this entry.
*/
function checkEntry(entry) {
	if (hasTag(entry, "Story")) return checkSingleStory(entry);
	if (hasTag(entry, "StoryGroup")) {
		const group = entry;
		return Object.entries(group.stories).flatMap(([variantName, variant]) => checkSingleStory(variant, group.title).map((d) => Object.freeze({
			storyName: `${group.title} / ${variantName}`,
			severity: d.severity,
			message: d.message
		})));
	}
	return [];
}
/**
* Check a single story for field count and prop validation issues.
*
* @private
* @param story - The story to check.
* @param groupTitle - Optional group title for qualified naming.
* @returns An array of diagnostics for this story.
*/
function checkSingleStory(story, groupTitle) {
	const name = match$1(groupTitle).with(P$1.string, (title) => `${title} / ${story.name}`).with(P$1.nullish, () => story.name).exhaustive();
	const fieldCountDiagnostics = buildFieldCountDiagnostics({
		editableFieldCount: countEditableFields(story),
		name
	});
	const propDiagnostics = validateProps({
		schema: story.schema,
		props: story.props
	}).map((fieldError) => Object.freeze({
		storyName: name,
		severity: "error",
		message: `Prop "${fieldError.field}": ${fieldError.message}`
	}));
	return [...fieldCountDiagnostics, ...propDiagnostics];
}
/**
* Build the diagnostics array for editable-field count violations.
*
* @private
* @param params - The current count and the qualified story name.
* @returns A single diagnostic when the count exceeds the max, else empty.
*/
function buildFieldCountDiagnostics(params) {
	if (params.editableFieldCount > 6) return [Object.freeze({
		storyName: params.name,
		severity: "error",
		message: `Too many editable fields: ${String(params.editableFieldCount)} (max ${String(6)}). Move fields to \`defaults\` to reduce.`
	})];
	return [];
}
/**
* Count the number of editable fields for a story (schema keys minus default keys).
*
* @private
* @param story - The story to inspect.
* @returns The number of editable fields.
*/
function countEditableFields(story) {
	const schemaKeys = Object.keys(story.schema.shape);
	const hiddenKeys = new Set(story.defaultKeys);
	return schemaKeys.filter((key) => !hiddenKeys.has(key)).length;
}
/**
* Count total individual stories across all entries.
*
* @private
* @param entries - The discovered story entries.
* @returns The total number of stories.
*/
function countStories(entries) {
	return [...entries.values()].reduce((count, entry) => {
		if (hasTag(entry, "Story")) return count + 1;
		if (hasTag(entry, "StoryGroup")) return count + Object.keys(entry.stories).length;
		return count;
	}, 0);
}
//#endregion
//#region src/stories/viewer/hooks/use-reload-state.ts
const MIN_RELOAD_DISPLAY_MS = 300;
/**
* Track reload state with a minimum display duration. When a reload starts,
* `isReloading` becomes `true` and stays `true` for at least
* {@link MIN_RELOAD_DISPLAY_MS} ms even if the actual reload completes faster.
*
* @returns A {@link ReloadState} with callbacks and current state.
*/
function useReloadState() {
	const [isReloading, setIsReloading] = useState(false);
	const reloadStartRef = useRef(0);
	const timerRef = useRef(null);
	const onReloadStart = useCallback(() => {
		reloadStartRef.current = Date.now();
		setIsReloading(true);
	}, []);
	useEffect(() => {
		const ref = timerRef;
		return () => {
			if (ref.current !== null) clearTimeout(ref.current);
		};
	}, []);
	return {
		isReloading,
		onReloadStart,
		onReloadEnd: useCallback(() => {
			const remaining = MIN_RELOAD_DISPLAY_MS - (Date.now() - reloadStartRef.current);
			if (timerRef.current !== null) clearTimeout(timerRef.current);
			if (remaining <= 0) {
				setIsReloading(false);
				return;
			}
			timerRef.current = setTimeout(() => {
				timerRef.current = null;
				setIsReloading(false);
			}, remaining);
		}, [])
	};
}
//#endregion
//#region src/stories/viewer/components/header.tsx
/**
* Header banner for the stories viewer TUI. Renders a book emoji
* alongside a styled "Stories" title.
*
* @returns A rendered header element.
*/
function Header() {
	return /* @__PURE__ */ jsxs(Box, {
		paddingX: 1,
		children: [/* @__PURE__ */ jsx(Text, { children: "📚 " }), /* @__PURE__ */ jsx(Text, {
			bold: true,
			color: "cyan",
			children: "Stories"
		})]
	});
}
//#endregion
//#region src/stories/viewer/components/help-overlay.tsx
const BROWSE_SHORTCUTS = [
	{
		key: "Up/Down",
		description: "Navigate story tree"
	},
	{
		key: "Enter",
		description: "Select story"
	},
	{
		key: "b",
		description: "Toggle sidebar"
	},
	{
		key: "r",
		description: "Reset props to defaults"
	},
	{
		key: "?",
		description: "Toggle help"
	},
	{
		key: "q",
		description: "Quit"
	}
];
const PREVIEW_SHORTCUTS = [
	{
		key: "Enter",
		description: "Edit props"
	},
	{
		key: "i",
		description: "Enter interactive mode"
	},
	{
		key: "r",
		description: "Reset props to defaults"
	},
	{
		key: "b",
		description: "Toggle sidebar"
	},
	{
		key: "Esc",
		description: "Back to browse"
	},
	{
		key: "?",
		description: "Toggle help"
	},
	{
		key: "q",
		description: "Quit"
	}
];
const EDIT_SHORTCUTS = [
	{
		key: "Tab",
		description: "Cycle to next prop"
	},
	{
		key: "Left/Right",
		description: "Switch between prop fields"
	},
	{
		key: "Esc",
		description: "Back to preview"
	}
];
const INTERACTIVE_SHORTCUTS = [{
	key: "Esc Esc",
	description: "Exit interactive mode"
}];
/**
* Modal overlay displaying available keyboard shortcuts for both
* browse and edit modes. Closes when the user presses `?` or `q`.
*
* @param props - The help overlay props.
* @returns A rendered help overlay element.
*/
function HelpOverlay({ onClose }) {
	useInput((input) => {
		if (input === "?" || input === "q") onClose();
	});
	return /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		borderStyle: "double",
		padding: 1,
		children: [
			/* @__PURE__ */ jsx(Text, {
				bold: true,
				children: "Keyboard Shortcuts"
			}),
			/* @__PURE__ */ jsx(Text, { children: " " }),
			/* @__PURE__ */ jsx(Text, {
				bold: true,
				color: "cyan",
				children: "Browse Mode"
			}),
			BROWSE_SHORTCUTS.map((shortcut) => /* @__PURE__ */ jsxs(Box, {
				gap: 2,
				children: [/* @__PURE__ */ jsx(Box, {
					width: 12,
					children: /* @__PURE__ */ jsx(Text, {
						color: "cyan",
						children: shortcut.key
					})
				}), /* @__PURE__ */ jsx(Text, { children: shortcut.description })]
			}, shortcut.key)),
			/* @__PURE__ */ jsx(Text, { children: " " }),
			/* @__PURE__ */ jsx(Text, {
				bold: true,
				color: "blue",
				children: "Preview Mode"
			}),
			PREVIEW_SHORTCUTS.map((shortcut) => /* @__PURE__ */ jsxs(Box, {
				gap: 2,
				children: [/* @__PURE__ */ jsx(Box, {
					width: 12,
					children: /* @__PURE__ */ jsx(Text, {
						color: "cyan",
						children: shortcut.key
					})
				}), /* @__PURE__ */ jsx(Text, { children: shortcut.description })]
			}, shortcut.key)),
			/* @__PURE__ */ jsx(Text, { children: " " }),
			/* @__PURE__ */ jsx(Text, {
				bold: true,
				color: "yellow",
				children: "Edit Mode"
			}),
			EDIT_SHORTCUTS.map((shortcut) => /* @__PURE__ */ jsxs(Box, {
				gap: 2,
				children: [/* @__PURE__ */ jsx(Box, {
					width: 12,
					children: /* @__PURE__ */ jsx(Text, {
						color: "cyan",
						children: shortcut.key
					})
				}), /* @__PURE__ */ jsx(Text, { children: shortcut.description })]
			}, shortcut.key)),
			/* @__PURE__ */ jsx(Text, { children: " " }),
			/* @__PURE__ */ jsx(Text, {
				bold: true,
				color: "green",
				children: "Interactive Mode"
			}),
			INTERACTIVE_SHORTCUTS.map((shortcut) => /* @__PURE__ */ jsxs(Box, {
				gap: 2,
				children: [/* @__PURE__ */ jsx(Box, {
					width: 12,
					children: /* @__PURE__ */ jsx(Text, {
						color: "cyan",
						children: shortcut.key
					})
				}), /* @__PURE__ */ jsx(Text, { children: shortcut.description })]
			}, shortcut.key))
		]
	});
}
//#endregion
//#region src/stories/viewer/utils.ts
/**
* Build include patterns from the optional CLI flag.
*
* @param include - Optional single glob pattern from CLI.
* @returns Array of include patterns, or undefined for defaults.
*/
function buildIncludePatterns(include) {
	if (include === void 0) return;
	return [include];
}
/**
* Apply a list of decorators to a component by reducing from left to right.
* Each decorator wraps the previous result.
*
* @param component - The base story component.
* @param decorators - The decorators to apply.
* @returns The fully decorated component.
*/
function applyDecorators(component, decorators) {
	return decorators.reduce((Comp, decorator) => decorator(Comp), component);
}
//#endregion
//#region src/stories/viewer/components/empty-state.tsx
/**
* Placeholder displayed when no story is currently selected in the viewer.
* Provides a hint to the user about how to browse stories.
*
* @returns A centered empty state element.
*/
function EmptyState() {
	return /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		flexGrow: 1,
		children: [/* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "No story selected"
		}), /* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "Use arrow keys to browse stories"
		})]
	});
}
//#endregion
//#region src/stories/viewer/components/error-boundary.tsx
/**
* Catch rendering errors in child components and display a fallback error
* message. This is the single allowed class component in the codebase
* because React does not provide a functional error boundary API.
*
* @remarks
* React mandates class components for error boundaries. This is the only
* exception to the "no classes" rule.
*/
var ErrorBoundary = class extends Component {
	state = { error: null };
	static getDerivedStateFromError(error) {
		return { error };
	}
	componentDidCatch(_error, _info) {}
	render() {
		if (this.state.error) return /* @__PURE__ */ jsxs(Box, {
			flexDirection: "column",
			padding: 1,
			children: [/* @__PURE__ */ jsx(Text, {
				color: "red",
				bold: true,
				children: "Render Error"
			}), /* @__PURE__ */ jsx(Text, {
				color: "red",
				children: this.state.error.message
			})]
		});
		return this.props.children;
	}
};
//#endregion
//#region src/stories/viewer/components/field-control.tsx
/**
* Dispatch to the appropriate input control based on the field control kind.
* Renders text inputs, selects, toggles, or read-only displays depending
* on the schema-derived control type.
*
* @param props - The field control props.
* @returns A rendered field control element.
*/
function FieldControl({ control, value, options, onChange, isFocused }) {
	return match$1(control).with("text", () => /* @__PURE__ */ jsx(TextInput, {
		defaultValue: valueToString(value),
		focused: isFocused,
		onSubmit: (submitted) => onChange(submitted)
	})).with("number", () => /* @__PURE__ */ jsx(TextInput, {
		defaultValue: valueToString(value),
		focused: isFocused,
		onSubmit: (submitted) => onChange(parseNumericValue(submitted))
	})).with("boolean", () => /* @__PURE__ */ jsx(Confirm, {
		defaultValue: match$1(value).with(true, () => true).otherwise(() => false),
		focused: isFocused,
		onSubmit: (submitted) => onChange(submitted)
	})).with("select", () => {
		return /* @__PURE__ */ jsx(Select, {
			focused: isFocused,
			options: buildSelectOptions(options),
			onChange: (selected) => onChange(selected)
		});
	}).with("multiselect", () => {
		return /* @__PURE__ */ jsxs(Box, {
			flexDirection: "column",
			children: [/* @__PURE__ */ jsx(MultiSelect, {
				focused: isFocused,
				options: buildSelectOptions(options),
				defaultValue: toStringArray(value),
				onSubmit: (selectedValues) => onChange(selectedValues)
			}), /* @__PURE__ */ jsxs(Text, {
				dimColor: true,
				children: [
					"(current: ",
					JSON.stringify(value),
					")"
				]
			})]
		});
	}).with("json", () => /* @__PURE__ */ jsx(TextInput, {
		defaultValue: stringifyJsonValue(value),
		focused: isFocused,
		onSubmit: (submitted) => onChange(parseJsonValue(submitted))
	})).with("readonly", () => /* @__PURE__ */ jsx(Text, {
		dimColor: true,
		children: valueToDisplay(value)
	})).exhaustive();
}
/**
* Convert a value to a string for use as a text input default. Returns
* an empty string for null and undefined values.
*
* @private
* @param value - The value to convert.
* @returns The string representation.
*/
function valueToString(value) {
	if (value === null || value === void 0) return "";
	return String(value);
}
/**
* Convert a value to a display string, using 'N/A' for null and
* undefined values.
*
* @private
* @param value - The value to convert.
* @returns The display string.
*/
function valueToDisplay(value) {
	if (value === null || value === void 0) return "N/A";
	return String(value);
}
/**
* Parse a string as a numeric value, falling back to 0 for invalid input.
*
* @private
* @param input - The string to parse.
* @returns The parsed number.
*/
function parseNumericValue(input) {
	const parsed = Number(input);
	if (Number.isNaN(parsed)) return 0;
	return parsed;
}
/**
* Build select-compatible option objects from a list of string values.
*
* @private
* @param options - The raw string options.
* @returns An array of label/value option objects.
*/
function buildSelectOptions(options) {
	if (options === void 0) return [];
	return options.map((opt) => ({
		label: opt,
		value: opt
	}));
}
/**
* Coerce a value into a string array. Returns an empty array when
* the value is not an array.
*
* @private
* @param value - The value to coerce.
* @returns A string array.
*/
function toStringArray(value) {
	if (Array.isArray(value)) return value.map(String);
	return [];
}
/**
* Safely stringify a value as JSON for display in a text input.
*
* @private
* @param value - The value to stringify.
* @returns The JSON string representation.
*/
function stringifyJsonValue(value) {
	if (value === null || value === void 0) return "";
	if (typeof value === "string") return value;
	return JSON.stringify(value);
}
/**
* Parse a JSON string, falling back to the raw string on parse failure.
*
* @private
* @param input - The string to parse.
* @returns The parsed value or the raw string.
*/
function parseJsonValue(input) {
	try {
		return JSON.parse(input);
	} catch {
		return input;
	}
}
//#endregion
//#region src/stories/viewer/components/props-editor.tsx
/**
* Custom dotted border style for the props editor inset.
*
* @private
*/
const DOTTED_BORDER = {
	topLeft: " ",
	top: "┄",
	topRight: " ",
	right: " ",
	bottomRight: " ",
	bottom: " ",
	bottomLeft: " ",
	left: " "
};
/**
* Inline form editor for story props. Renders inside the preview panel
* with a dotted border to visually separate it from the component preview.
* When there are 2 or more fields, each field is shown in its own tab
* to avoid key conflicts. A single field renders inline without tabs.
*
* @param props - The props editor props.
* @returns A rendered props editor element.
*/
function PropsEditor({ fields, values, errors, onChange, isFocused }) {
	if (fields.length === 0) return /* @__PURE__ */ jsxs(Box, {
		marginTop: 1,
		borderStyle: DOTTED_BORDER,
		borderDimColor: true,
		borderTop: true,
		borderBottom: false,
		borderLeft: false,
		borderRight: false,
		paddingX: 1,
		children: [/* @__PURE__ */ jsx(Text, {
			bold: true,
			dimColor: true,
			children: "Props"
		}), /* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: " — none"
		})]
	});
	if (fields.length === 1) return /* @__PURE__ */ jsx(SingleFieldEditor, {
		field: fields[0],
		values,
		errors,
		onChange,
		isFocused
	});
	return /* @__PURE__ */ jsx(TabbedFieldEditor, {
		fields,
		values,
		errors,
		onChange,
		isFocused
	});
}
/**
* Render a single field inline without tabs.
*
* @private
* @param props - The single field editor props.
* @returns A rendered single field editor element.
*/
function SingleFieldEditor({ field, values, errors, onChange, isFocused }) {
	const fieldError = findFieldError(errors, field.name);
	return /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		marginTop: 1,
		borderStyle: DOTTED_BORDER,
		borderDimColor: true,
		borderTop: true,
		borderBottom: false,
		borderLeft: false,
		borderRight: false,
		paddingX: 1,
		children: [/* @__PURE__ */ jsx(Box, {
			marginBottom: 1,
			children: /* @__PURE__ */ jsx(Text, {
				bold: true,
				children: "Props"
			})
		}), /* @__PURE__ */ jsxs(Box, {
			flexDirection: "column",
			children: [
				/* @__PURE__ */ jsxs(Box, {
					gap: 1,
					children: [/* @__PURE__ */ jsx(Box, {
						width: 16,
						children: /* @__PURE__ */ jsxs(Text, {
							bold: isFocused,
							color: resolveFieldColor(isFocused),
							children: [field.name, requiredMarker(field.isOptional)]
						})
					}), /* @__PURE__ */ jsx(FieldControl, {
						control: field.control,
						value: values[field.name],
						options: field.options,
						onChange: (value) => onChange(field.name, value),
						isFocused
					})]
				}),
				/* @__PURE__ */ jsx(FieldErrorMessage, { error: fieldError }),
				/* @__PURE__ */ jsx(SubmitHint, {
					control: field.control,
					isFocused
				})
			]
		})]
	});
}
/**
* Render fields as tabs, one field per tab.
*
* @private
* @param props - The tabbed field editor props.
* @returns A rendered tabbed field editor element.
*/
function TabbedFieldEditor({ fields, values, errors, onChange, isFocused }) {
	const tabs = fields.map((field) => ({
		label: field.name,
		content: /* @__PURE__ */ jsx(FieldTab, {
			field,
			values,
			errors,
			onChange,
			isFocused
		})
	}));
	return /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		marginTop: 1,
		borderStyle: DOTTED_BORDER,
		borderDimColor: true,
		borderTop: true,
		borderBottom: false,
		borderLeft: false,
		borderRight: false,
		paddingX: 1,
		children: [/* @__PURE__ */ jsx(Box, {
			marginBottom: 1,
			children: /* @__PURE__ */ jsx(Text, {
				bold: true,
				children: "Props"
			})
		}), /* @__PURE__ */ jsx(Tabs, {
			tabs,
			isFocused
		})]
	});
}
/**
* Content for a single field tab. Renders the field control with its
* label and any validation error.
*
* @private
* @param props - The field tab props.
* @returns A rendered field tab content element.
*/
function FieldTab({ field, values, errors, onChange, isFocused }) {
	const fieldError = findFieldError(errors, field.name);
	return /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		paddingTop: 1,
		children: [
			/* @__PURE__ */ jsxs(Box, {
				gap: 1,
				children: [
					/* @__PURE__ */ jsx(Text, {
						dimColor: true,
						children: "type:"
					}),
					/* @__PURE__ */ jsx(Text, { children: field.zodTypeName }),
					match$1(field.isOptional).with(true, () => /* @__PURE__ */ jsx(Text, {
						dimColor: true,
						children: " (optional)"
					})).with(false, () => /* @__PURE__ */ jsxs(Text, {
						color: "red",
						dimColor: true,
						children: [" ", "(required)"]
					})).exhaustive()
				]
			}),
			/* @__PURE__ */ jsx(FieldDescription, { description: field.description }),
			/* @__PURE__ */ jsxs(Box, {
				gap: 1,
				marginTop: 1,
				children: [/* @__PURE__ */ jsx(Text, {
					bold: isFocused,
					color: resolveFieldColor(isFocused),
					children: "value:"
				}), /* @__PURE__ */ jsx(FieldControl, {
					control: field.control,
					value: values[field.name],
					options: field.options,
					onChange: (value) => onChange(field.name, value),
					isFocused
				})]
			}),
			/* @__PURE__ */ jsx(FieldErrorMessage, { error: fieldError }),
			/* @__PURE__ */ jsx(SubmitHint, {
				control: field.control,
				isFocused
			})
		]
	});
}
/**
* Render a dimmed submit hint below the field control. Shows a
* context-appropriate message based on the control type. Hidden
* when the field is not focused or the control is read-only.
*
* @private
* @param props - The component props.
* @returns A rendered hint element or null.
*/
function SubmitHint({ control, isFocused }) {
	if (!isFocused) return null;
	const hint = resolveSubmitHint(control);
	if (hint === null) return null;
	return /* @__PURE__ */ jsx(Box, {
		marginTop: 1,
		children: /* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: hint
		})
	});
}
/**
* Resolve the submit hint text for a given control kind.
*
* @private
* @param control - The field control kind.
* @returns The hint string, or null for controls that don't need one.
*/
function resolveSubmitHint(control) {
	return match$1(control).with("text", () => "press enter to submit").with("number", () => "press enter to submit").with("json", () => "press enter to submit").with("boolean", () => "y to confirm, n to cancel").with("select", () => "use ↑↓ to choose, enter to select").with("multiselect", () => "use ↑↓ to navigate, space to toggle, enter to submit").with("readonly", () => null).exhaustive();
}
/**
* Render a field description when available, or nothing when absent.
*
* @private
* @param props - The component props.
* @returns A rendered description element or null.
*/
function FieldDescription({ description }) {
	if (description === void 0) return null;
	return /* @__PURE__ */ jsxs(Box, {
		gap: 1,
		children: [/* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "desc:"
		}), /* @__PURE__ */ jsx(Text, {
			dimColor: true,
			italic: true,
			children: description
		})]
	});
}
/**
* Resolve the color for a field label based on focus state.
*
* @private
* @param isFocused - Whether the field is focused.
* @returns The color string, or undefined for default.
*/
function resolveFieldColor(isFocused) {
	return match$1(isFocused).with(true, () => "cyan").with(false, () => void 0).exhaustive();
}
/**
* Return the required marker suffix for a field label.
*
* @private
* @param isOptional - Whether the field is optional.
* @returns An empty string for optional fields, '*' for required fields.
*/
function requiredMarker(isOptional) {
	if (isOptional) return "";
	return "*";
}
/**
* Render a field error message, or nothing when no error exists.
*
* @private
* @param props - The component props.
* @returns A rendered error message or null.
*/
function FieldErrorMessage({ error }) {
	if (error === null) return null;
	return /* @__PURE__ */ jsx(Box, {
		paddingLeft: 2,
		children: /* @__PURE__ */ jsx(Text, {
			color: "red",
			children: error.message
		})
	});
}
/**
* Find the first error matching a given field name. Matches both exact field
* names and nested dot-path errors (e.g. `meta.nested` or `tags.0`) that
* belong to the owning field.
*
* @private
* @param errors - The array of field errors.
* @param fieldName - The field name to search for.
* @returns The matching error, or null if none found.
*/
function findFieldError(errors, fieldName) {
	const found = errors.find((error) => error.field === fieldName || error.field.startsWith(`${fieldName}.`));
	if (found === void 0) return null;
	return found;
}
//#endregion
//#region src/stories/viewer/components/preview.tsx
/** Minimum rows allocated to the props editor area. */
const MIN_PROPS_ROWS = 6;
/** Maximum rows allocated to the props editor area. */
const MAX_PROPS_ROWS = 14;
/**
* Preview panel that renders the selected story component with the current
* props and an inline props editor. Shows the qualified story name, file path,
* and description above the rendered component. The props editor is inset
* below the component with a dotted separator. Both the component area and
* the props editor are independently scrollable to prevent overflow.
* Applies decorators in order and wraps the result in an
* {@link ErrorBoundary} to catch render errors.
*
* @param props - The preview props.
* @returns A rendered preview element.
*/
function Preview({ story, currentProps, context, fields, errors, onPropsChange, isFocused, editable = false, borderless = false, interactive = false }) {
	const contentRef = useRef(null);
	const { height: contentHeight } = useSize(contentRef);
	const { componentAreaHeight, propsAreaHeight } = useMemo(() => splitContentHeight(contentHeight), [contentHeight]);
	const DecoratedComponent = useMemo(() => {
		if (story === null) return null;
		return applyDecorators(story.component, story.decorators);
	}, [story]);
	if (story === null || context === null || DecoratedComponent === null) return /* @__PURE__ */ jsxs(Box, {
		borderStyle: match$1(borderless).with(true, () => void 0).with(false, () => "single").exhaustive(),
		borderDimColor: !borderless,
		flexDirection: "column",
		flexGrow: 1,
		overflow: "hidden",
		children: [/* @__PURE__ */ jsx(Box, {
			paddingX: 1,
			children: /* @__PURE__ */ jsx(Text, {
				bold: true,
				dimColor: true,
				children: "Preview"
			})
		}), /* @__PURE__ */ jsx(EmptyState, {})]
	});
	if (interactive) return /* @__PURE__ */ jsx(Box, {
		flexDirection: "column",
		flexGrow: 1,
		overflow: "hidden",
		children: /* @__PURE__ */ jsx(ErrorBoundary, { children: /* @__PURE__ */ jsx(DecoratedComponent, { ...currentProps }) }, context.displayName)
	});
	return /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		flexGrow: 1,
		overflow: "hidden",
		borderStyle: match$1(borderless).with(true, () => void 0).with(false, () => "single").exhaustive(),
		borderDimColor: match$1(borderless).with(true, () => false).with(false, () => !isFocused).exhaustive(),
		borderColor: match$1({
			isFocused,
			borderless
		}).with({ borderless: true }, () => void 0).with({ isFocused: true }, () => "cyan").otherwise(() => void 0),
		paddingX: 1,
		children: [/* @__PURE__ */ jsx(PreviewHeader, { context }), /* @__PURE__ */ jsxs(Box, {
			ref: contentRef,
			flexDirection: "column",
			flexGrow: 1,
			children: [/* @__PURE__ */ jsx(ScrollArea, {
				height: Math.max(1, componentAreaHeight),
				children: /* @__PURE__ */ jsx(ErrorBoundary, { children: /* @__PURE__ */ jsx(DecoratedComponent, {
					...currentProps,
					focused: false
				}) }, context.displayName)
			}), /* @__PURE__ */ jsx(Box, {
				height: propsAreaHeight,
				overflow: "hidden",
				flexDirection: "column",
				children: /* @__PURE__ */ jsx(PropsEditor, {
					fields,
					values: currentProps,
					errors,
					onChange: onPropsChange,
					isFocused: editable
				})
			})]
		})]
	});
}
/**
* Render the preview header showing story name, file path, and description.
* "Preview" is displayed as a bold section label, then the story name,
* path, and optional description below.
*
* @private
* @param props - The header props.
* @returns A rendered header element.
*/
function PreviewHeader({ context }) {
	return /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		marginBottom: 1,
		children: [
			/* @__PURE__ */ jsx(Box, {
				marginBottom: 1,
				children: /* @__PURE__ */ jsx(Text, {
					bold: true,
					children: "Preview"
				})
			}),
			/* @__PURE__ */ jsx(Text, {
				bold: true,
				children: context.displayName
			}),
			/* @__PURE__ */ jsx(Text, {
				italic: true,
				dimColor: true,
				children: context.filePath
			}),
			/* @__PURE__ */ jsx(StoryDescription, { description: context.description })
		]
	});
}
/**
* Render a story description when available, or nothing when absent.
*
* @private
* @param props - The component props.
* @returns A rendered description element or null.
*/
function StoryDescription({ description }) {
	if (description === void 0) return null;
	return /* @__PURE__ */ jsx(Text, {
		dimColor: true,
		italic: true,
		children: description
	});
}
/**
* Split the measured content area height between the component preview
* and the props editor. The props editor receives up to 45% of the
* available space (clamped between {@link MIN_PROPS_ROWS} and
* {@link MAX_PROPS_ROWS}). The component preview gets the remainder.
*
* @private
* @param contentHeight - Total measured height of the content area.
* @returns Frozen object with computed area heights.
*/
function splitContentHeight(contentHeight) {
	const propsAreaHeight = Math.min(Math.max(MIN_PROPS_ROWS, Math.floor(contentHeight * .45)), MAX_PROPS_ROWS);
	const componentAreaHeight = Math.max(1, contentHeight - propsAreaHeight);
	return Object.freeze({
		componentAreaHeight,
		propsAreaHeight
	});
}
//#endregion
//#region src/stories/viewer/components/sidebar.tsx
/**
* Story browser sidebar with a collapsible directory tree. Groups can
* be expanded/collapsed with Enter. Story leaves are selected with Enter.
* Arrow keys navigate the visible tree.
*
* @param props - The sidebar props.
* @returns A rendered sidebar element.
*/
function Sidebar({ entries, selectedId, onSelect, isFocused, hidden }) {
	const allNodes = useMemo(() => buildTreeNodes(entries), [entries]);
	const [collapsed, setCollapsed] = useState(() => initialCollapsedSet(allNodes));
	const visibleNodes = useMemo(() => filterVisibleNodes(allNodes, collapsed), [allNodes, collapsed]);
	const [highlightIndex, setHighlightIndex] = useState(0);
	useEffect(() => {
		setHighlightIndex((current) => {
			if (visibleNodes.length === 0) return 0;
			if (current >= visibleNodes.length) return visibleNodes.length - 1;
			return current;
		});
	}, [visibleNodes.length]);
	useInput((_input, key) => {
		if (key.upArrow) setHighlightIndex((current) => match$1({
			atStart: current <= 0,
			empty: visibleNodes.length === 0
		}).with({ empty: true }, () => 0).with({ atStart: true }, () => visibleNodes.length - 1).otherwise(() => current - 1));
		if (key.downArrow) setHighlightIndex((current) => match$1({
			atEnd: current >= visibleNodes.length - 1,
			empty: visibleNodes.length === 0
		}).with({ empty: true }, () => 0).with({ atEnd: true }, () => 0).otherwise(() => current + 1));
		if (key.return) {
			const node = visibleNodes[highlightIndex];
			if (node === void 0) return;
			if (node.kind === "group") {
				toggleGroup(node.id, setCollapsed);
				return;
			}
			onSelect(node.id);
		}
	}, { isActive: isFocused });
	const highlightedNode = visibleNodes[highlightIndex];
	const highlightedId = resolveNodeId(highlightedNode);
	const scrollRef = useRef(null);
	const { height: scrollHeight } = useSize(scrollRef);
	return /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		borderStyle: "single",
		borderDimColor: !isFocused,
		borderColor: match$1(isFocused).with(true, () => "cyan").with(false, () => void 0).exhaustive(),
		width: "25%",
		paddingX: 1,
		display: match$1(hidden === true).with(true, () => "none").with(false, () => "flex").exhaustive(),
		children: [/* @__PURE__ */ jsx(Box, {
			marginBottom: 1,
			children: /* @__PURE__ */ jsx(Text, {
				bold: true,
				dimColor: !isFocused,
				children: "Stories"
			})
		}), /* @__PURE__ */ jsx(Box, {
			ref: scrollRef,
			flexDirection: "column",
			flexGrow: 1,
			children: /* @__PURE__ */ jsx(ScrollArea, {
				height: Math.max(1, scrollHeight),
				activeIndex: Math.max(0, highlightIndex),
				itemCount: visibleNodes.length,
				showIndicator: visibleNodes.length > scrollHeight,
				children: visibleNodes.map((node) => /* @__PURE__ */ jsx(TreeRow, {
					node,
					isHighlighted: node.id === highlightedId,
					isSelected: node.id === selectedId,
					isCollapsed: collapsed.has(node.id)
				}, node.id))
			})
		})]
	});
}
/**
* Render a single row in the sidebar tree.
*
* @private
* @param props - The tree row props.
* @returns A rendered tree row element.
*/
function TreeRow({ node, isHighlighted, isSelected, isCollapsed }) {
	const indent = "  ".repeat(node.indent);
	if (node.kind === "group") {
		const icon = match$1(isCollapsed).with(true, () => "▸ 📁").with(false, () => "▾ 📂").exhaustive();
		return /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsxs(Text, {
			bold: true,
			dimColor: !isHighlighted,
			color: match$1(isHighlighted).with(true, () => "cyan").with(false, () => void 0).exhaustive(),
			children: [
				indent,
				icon,
				" ",
				node.label
			]
		}) });
	}
	const cursor = match$1(isHighlighted).with(true, () => "▸ ").with(false, () => "  ").exhaustive();
	return /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsxs(Text, {
		color: match$1({
			isHighlighted,
			isSelected
		}).with({ isHighlighted: true }, () => "cyan").with({ isSelected: true }, () => "green").otherwise(() => void 0),
		bold: isHighlighted,
		children: [
			indent,
			cursor,
			node.label
		]
	}) });
}
/**
* Toggle a group's collapsed state.
*
* @private
* @param groupId - The group node ID to toggle.
* @param setCollapsed - State setter for the collapsed set.
*/
function toggleGroup(groupId, setCollapsed) {
	setCollapsed((prev) => {
		const next = new Set(prev);
		if (next.has(groupId)) next.delete(groupId);
		else next.add(groupId);
		return next;
	});
}
/**
* Build the full tree node list from registry entries. Groups produce
* a header node followed by indented leaf nodes for each variant.
* Single stories produce a single leaf node at indent 0.
*
* @private
* @param entries - The story registry entries.
* @returns The full (unfiltered) tree node list.
*/
function buildTreeNodes(entries) {
	return [...entries.entries()].flatMap(([key, entry]) => match$1(hasTag(entry, "StoryGroup")).with(true, () => groupToNodes(key, entry)).with(false, () => match$1(hasTag(entry, "Story")).with(true, () => [{
		id: key,
		label: entry.name,
		kind: "leaf",
		indent: 0
	}]).with(false, () => []).exhaustive()).exhaustive());
}
/**
* Convert a story group into tree nodes: one group header followed by
* indented leaf nodes for each variant.
*
* @private
* @param key - The group registry key.
* @param group - The story group entry.
* @returns Tree nodes for the group.
*/
function groupToNodes(key, group) {
	return [{
		id: `group:${key}`,
		label: group.title,
		kind: "group",
		indent: 0,
		groupKey: key
	}, ...Object.keys(group.stories).map((variantName) => ({
		id: `${key}::${variantName}`,
		label: variantName,
		kind: "leaf",
		indent: 1,
		groupKey: key
	}))];
}
/**
* Filter the full tree to only visible nodes based on collapsed groups.
* When a group is collapsed, its child leaves are hidden.
*
* @private
* @param nodes - The full tree node list.
* @param collapsed - The set of collapsed group node IDs.
* @returns Only the nodes that should be visible.
*/
function filterVisibleNodes(nodes, collapsed) {
	return nodes.filter((node) => {
		if (node.kind === "group") return true;
		if (node.groupKey === void 0) return true;
		return !collapsed.has(`group:${node.groupKey}`);
	});
}
/**
* Extract the ID from a tree node, returning null when undefined.
*
* @private
* @param node - The tree node to resolve.
* @returns The node ID or null.
*/
function resolveNodeId(node) {
	if (node === void 0) return null;
	return node.id;
}
/**
* Build the initial collapsed set containing all group node IDs,
* so groups start collapsed by default.
*
* @private
* @param nodes - The full tree node list.
* @returns A set of all group node IDs.
*/
function initialCollapsedSet(nodes) {
	return new Set(nodes.filter((node) => node.kind === "group").map((node) => node.id));
}
//#endregion
//#region src/stories/viewer/components/status-bar.tsx
/**
* Bottom status bar displaying mode indicator and context-sensitive
* keyboard shortcut hints.
*
* @param props - The status bar props.
* @returns A rendered status bar element.
*/
function StatusBar({ mode, hasSelection, isReloading }) {
	return /* @__PURE__ */ jsxs(Box, {
		paddingX: 1,
		paddingTop: 1,
		children: [
			/* @__PURE__ */ jsx(ModeIndicator, { mode }),
			/* @__PURE__ */ jsx(Text, { children: " " }),
			/* @__PURE__ */ jsx(Text, {
				dimColor: true,
				children: "│"
			}),
			/* @__PURE__ */ jsx(Text, { children: " " }),
			match$1({
				isReloading,
				mode
			}).with({ isReloading: true }, () => /* @__PURE__ */ jsx(Text, {
				bold: true,
				color: "yellow",
				children: "Reloading..."
			})).with({
				isReloading: false,
				mode: "browse"
			}, () => /* @__PURE__ */ jsx(BrowseHints, { hasSelection })).with({
				isReloading: false,
				mode: "preview"
			}, () => /* @__PURE__ */ jsx(PreviewHints, {})).with({
				isReloading: false,
				mode: "edit"
			}, () => /* @__PURE__ */ jsx(EditHints, {})).with({
				isReloading: false,
				mode: "interactive"
			}, () => /* @__PURE__ */ jsx(InteractiveHints, {})).exhaustive(),
			/* @__PURE__ */ jsx(Spacer, {}),
			match$1(mode).with("browse", () => /* @__PURE__ */ jsx(QuitHint, {})).with("preview", () => /* @__PURE__ */ jsx(QuitHint, {})).otherwise(() => null)
		]
	});
}
/**
* Render the mode indicator badge.
*
* @private
* @param props - The mode indicator props.
* @returns A rendered mode indicator element.
*/
function ModeIndicator({ mode }) {
	return /* @__PURE__ */ jsx(Text, {
		bold: true,
		color: match$1(mode).with("browse", () => "cyan").with("preview", () => "blue").with("edit", () => "yellow").with("interactive", () => "green").exhaustive(),
		children: match$1(mode).with("browse", () => "● Browse").with("preview", () => "● Preview").with("edit", () => "● Edit").with("interactive", () => "● Interactive").exhaustive()
	});
}
/**
* Render keyboard hints for browse mode.
*
* @private
* @param props - The browse hints props.
* @returns A rendered hints element.
*/
function BrowseHints({ hasSelection }) {
	return /* @__PURE__ */ jsxs(Box, { children: [
		/* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "↑↓"
		}),
		/* @__PURE__ */ jsx(Text, { children: ": navigate" }),
		/* @__PURE__ */ jsx(Text, { children: " " }),
		/* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "enter"
		}),
		/* @__PURE__ */ jsx(Text, { children: ": select/expand" }),
		/* @__PURE__ */ jsx(Text, { children: " " }),
		/* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "b"
		}),
		/* @__PURE__ */ jsx(Text, { children: ": sidebar" }),
		/* @__PURE__ */ jsx(Text, { children: " " }),
		/* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "?"
		}),
		/* @__PURE__ */ jsx(Text, { children: ": help" }),
		match$1(hasSelection).with(true, () => /* @__PURE__ */ jsxs(Box, { children: [
			/* @__PURE__ */ jsx(Text, { children: " " }),
			/* @__PURE__ */ jsx(Text, {
				dimColor: true,
				children: "r"
			}),
			/* @__PURE__ */ jsx(Text, { children: ": reset" })
		] })).with(false, () => null).exhaustive()
	] });
}
/**
* Render keyboard hints for preview mode.
*
* @private
* @returns A rendered hints element.
*/
function PreviewHints() {
	return /* @__PURE__ */ jsxs(Box, { children: [
		/* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "enter"
		}),
		/* @__PURE__ */ jsx(Text, { children: ": edit props" }),
		/* @__PURE__ */ jsx(Text, { children: " " }),
		/* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "i"
		}),
		/* @__PURE__ */ jsx(Text, { children: ": interactive" }),
		/* @__PURE__ */ jsx(Text, { children: " " }),
		/* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "r"
		}),
		/* @__PURE__ */ jsx(Text, { children: ": reset" }),
		/* @__PURE__ */ jsx(Text, { children: " " }),
		/* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "esc"
		}),
		/* @__PURE__ */ jsx(Text, { children: ": back" }),
		/* @__PURE__ */ jsx(Text, { children: " " }),
		/* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "?"
		}),
		/* @__PURE__ */ jsx(Text, { children: ": help" })
	] });
}
/**
* Render keyboard hints for edit mode.
*
* @private
* @returns A rendered hints element.
*/
function EditHints() {
	return /* @__PURE__ */ jsxs(Box, { children: [
		/* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "tab/←→"
		}),
		/* @__PURE__ */ jsx(Text, { children: ": prop" }),
		/* @__PURE__ */ jsx(Text, { children: " " }),
		/* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: "esc"
		}),
		/* @__PURE__ */ jsx(Text, { children: ": back" })
	] });
}
/**
* Render keyboard hints for interactive mode.
*
* @private
* @returns A rendered hints element.
*/
function InteractiveHints() {
	return /* @__PURE__ */ jsxs(Box, { children: [/* @__PURE__ */ jsx(Text, {
		dimColor: true,
		children: "esc esc"
	}), /* @__PURE__ */ jsx(Text, { children: ": exit interactive" })] });
}
/**
* Render the quit shortcut hint.
*
* @private
* @returns A rendered quit hint element.
*/
function QuitHint() {
	return /* @__PURE__ */ jsxs(Box, { children: [/* @__PURE__ */ jsx(Text, {
		dimColor: true,
		children: "q"
	}), /* @__PURE__ */ jsx(Text, { children: ": quit" })] });
}
//#endregion
//#region src/stories/viewer/hooks/use-panel-focus.ts
/**
* Manage the interaction mode between browsing, previewing, editing, and
* interactive control. The viewer starts in browse mode. Selecting a story
* enters preview mode. From preview, the user can enter edit or interactive
* mode. Exiting returns one level up.
*
* @returns The current viewer mode state and control functions.
*/
function useViewerMode() {
	const [mode, setMode] = useState("browse");
	return {
		mode,
		enterPreviewMode: useCallback(() => {
			setMode("preview");
		}, []),
		exitPreviewMode: useCallback(() => {
			setMode("browse");
		}, []),
		enterEditMode: useCallback(() => {
			setMode("edit");
		}, []),
		exitEditMode: useCallback(() => {
			setMode("preview");
		}, []),
		enterInteractiveMode: useCallback(() => {
			setMode("interactive");
		}, []),
		exitInteractiveMode: useCallback(() => {
			setMode("preview");
		}, [])
	};
}
//#endregion
//#region src/stories/viewer/hooks/use-stories.ts
/**
* Subscribe to the story registry and return the current snapshot of all
* registered story entries. Re-renders when the registry changes.
*
* @param registry - The story registry to observe.
* @returns A read-only map of story entries keyed by name.
*/
function useStories(registry) {
	return useSyncExternalStore(registry.subscribe, registry.getSnapshot);
}
//#endregion
//#region src/stories/viewer/stories-app.tsx
/**
* Root layout component for the stories viewer TUI. Combines the sidebar,
* preview, props editor, and status bar into a fullscreen interactive
* terminal application.
*
* Operates in four modes:
* - **browse** — Sidebar is active, user navigates the story tree.
* - **preview** — Preview panel focused, story and props visible but read-only.
* - **edit** — Props editor is active, user edits field values.
* - **interactive** — Story has full terminal control, props are hidden.
*
* @param props - The stories app props.
* @returns A rendered stories app element.
*/
function StoriesApp({ registry, isReloading }) {
	const entries = useStories(registry);
	const { mode, enterPreviewMode, exitPreviewMode, enterEditMode, exitEditMode, enterInteractiveMode, exitInteractiveMode } = useViewerMode();
	const [selectedStoryId, setSelectedStoryId] = useState(null);
	const [currentProps, setCurrentProps] = useState({});
	const [showHelp, setShowHelp] = useState(false);
	const [showSidebar, setShowSidebar] = useState(true);
	const { exit } = useApp();
	const selectedStory = useMemo(() => resolveStory(entries, selectedStoryId), [entries, selectedStoryId]);
	useEffect(() => {
		if (entries.size === 0 || selectedStoryId !== null) return;
		const firstId = findFirstLeafId(entries);
		if (firstId === null) return;
		const resolved = resolveStory(entries, firstId);
		setSelectedStoryId(firstId);
		if (resolved !== null) setCurrentProps({ ...resolved.props });
	}, [entries, selectedStoryId]);
	const previewContext = useMemo(() => buildPreviewContext(entries, selectedStoryId, selectedStory), [
		entries,
		selectedStoryId,
		selectedStory
	]);
	const fields = useMemo(() => {
		if (selectedStory === null) return [];
		const allFields = schemaToFieldDescriptors(selectedStory.schema);
		const hiddenKeys = new Set(selectedStory.defaultKeys);
		return allFields.filter((field) => !hiddenKeys.has(field.name));
	}, [selectedStory]);
	const errors = useMemo(() => {
		if (selectedStory === null) return [];
		return validateProps({
			schema: selectedStory.schema,
			props: currentProps
		});
	}, [selectedStory, currentProps]);
	const handleSelect = useCallback((id) => {
		const resolved = resolveStory(entries, id);
		setSelectedStoryId(id);
		if (resolved !== null) setCurrentProps({ ...resolved.props });
		enterPreviewMode();
	}, [entries, enterPreviewMode]);
	const handlePropsChange = useCallback((name, value) => {
		setCurrentProps((prev) => ({
			...prev,
			[name]: value
		}));
	}, []);
	const handleResetProps = useCallback(() => {
		if (selectedStory !== null) setCurrentProps({ ...selectedStory.props });
	}, [selectedStory]);
	const handleCloseHelp = useCallback(() => {
		setShowHelp(false);
	}, []);
	useHotkey({
		keys: ["escape escape"],
		action: exitInteractiveMode,
		active: mode === "interactive"
	});
	useInput((_input, key) => {
		if (key.escape) exitEditMode();
	}, { isActive: mode === "edit" });
	useInput((input, key) => {
		if (showHelp) return;
		if (key.return && selectedStory !== null) {
			enterEditMode();
			return;
		}
		if (key.escape) exitPreviewMode();
		if (input === "i" && selectedStory !== null) enterInteractiveMode();
		if (input === "r") handleResetProps();
		if (input === "q") exit();
		if (input === "?") setShowHelp(true);
		if (input === "b") setShowSidebar((prev) => !prev);
	}, { isActive: mode === "preview" });
	useInput((input, _key) => {
		if (showHelp) return;
		if (input === "q") exit();
		if (input === "r") handleResetProps();
		if (input === "?") setShowHelp(true);
		if (input === "b") setShowSidebar((prev) => !prev);
	}, { isActive: mode === "browse" });
	if (showHelp) return /* @__PURE__ */ jsx(FullScreen, { children: /* @__PURE__ */ jsx(HelpOverlay, { onClose: handleCloseHelp }) });
	const isInteractive = mode === "interactive";
	return /* @__PURE__ */ jsx(FullScreen, { children: /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		flexGrow: 1,
		children: [
			match$1(isInteractive).with(false, () => /* @__PURE__ */ jsx(Header, {})).with(true, () => null).exhaustive(),
			/* @__PURE__ */ jsxs(Box, {
				flexDirection: "row",
				flexGrow: 1,
				overflow: "hidden",
				children: [match$1(isInteractive).with(false, () => /* @__PURE__ */ jsx(Sidebar, {
					entries,
					selectedId: selectedStoryId,
					onSelect: handleSelect,
					isFocused: mode === "browse" && showSidebar,
					hidden: !showSidebar
				})).with(true, () => null).exhaustive(), /* @__PURE__ */ jsx(Box, {
					flexDirection: "column",
					flexGrow: 1,
					children: match$1(isReloading).with(true, () => /* @__PURE__ */ jsx(ReloadOverlay, {})).with(false, () => /* @__PURE__ */ jsx(Preview, {
						story: selectedStory,
						currentProps,
						context: previewContext,
						fields,
						errors,
						onPropsChange: handlePropsChange,
						isFocused: mode === "preview" || mode === "edit",
						editable: mode === "edit",
						borderless: isInteractive || !showSidebar,
						interactive: isInteractive
					})).exhaustive()
				})]
			}),
			/* @__PURE__ */ jsx(StatusBar, {
				mode,
				hasSelection: selectedStoryId !== null,
				isReloading
			})
		]
	}) });
}
/**
* Parse a story ID into its constituent parts.
*
* Direct story IDs contain no separator. Group variant IDs use the
* `groupKey::variantName` format.
*
* @private
* @param id - The raw story ID string.
* @returns The parsed story ID.
*/
function parseStoryId(id) {
	const separatorIndex = id.indexOf("::");
	if (separatorIndex === -1) return {
		type: "single",
		key: id
	};
	return {
		type: "group",
		groupKey: id.slice(0, separatorIndex),
		variantName: id.slice(separatorIndex + 2)
	};
}
/**
* Resolve a story from the entries map by its ID. Supports both direct
* story keys and group variant keys in the format `groupKey::variantName`.
*
* @private
* @param entries - The story registry entries.
* @param id - The story ID to resolve.
* @returns The resolved story, or null if not found.
*/
function resolveStory(entries, id) {
	if (id === null) return null;
	return match$1(parseStoryId(id)).with({ type: "single" }, ({ key }) => {
		const entry = entries.get(key);
		if (entry === void 0 || !hasTag(entry, "Story")) return null;
		return entry;
	}).with({ type: "group" }, ({ groupKey, variantName }) => {
		const group = entries.get(groupKey);
		if (group === void 0 || !hasTag(group, "StoryGroup")) return null;
		return group.stories[variantName] ?? null;
	}).exhaustive();
}
/**
* Build the preview context from the selected story ID and resolved story.
* Computes the relative file path and a qualified display name
* (e.g. "LogLevel > Info" for group variants, "StatusBadge" for singles).
*
* @private
* @param entries - The story registry entries.
* @param id - The selected story ID.
* @param story - The resolved story, or null.
* @returns A preview context, or null when no story is selected.
*/
function buildPreviewContext(entries, id, story) {
	if (id === null || story === null) return null;
	const parsed = parseStoryId(id);
	const cwd = process$1.cwd();
	return match$1(parsed).with({ type: "single" }, ({ key }) => ({
		filePath: relative(cwd, key),
		displayName: story.name,
		description: story.description
	})).with({ type: "group" }, ({ groupKey, variantName }) => {
		const groupTitle = resolveGroupTitle(entries.get(groupKey));
		return {
			filePath: relative(cwd, groupKey),
			displayName: `${groupTitle} > ${variantName}`,
			description: story.description
		};
	}).exhaustive();
}
/**
* Extract the title from a story group entry, falling back to 'Unknown'.
*
* @private
* @param entry - The story entry to inspect.
* @returns The group title.
*/
function resolveGroupTitle(entry) {
	if (entry === void 0) return "Unknown";
	if (hasTag(entry, "StoryGroup")) return entry.title;
	return "Unknown";
}
/**
* Find the first selectable story ID from the entries map. For single
* stories this is the entry key. For groups, it is the first variant
* key in `groupKey::variantName` format.
*
* @private
* @param entries - The story registry entries.
* @returns The first leaf story ID, or null if the map is empty.
*/
function findFirstLeafId(entries) {
	const first = entries.entries().next();
	if (first.done) return null;
	const [key, entry] = first.value;
	if (hasTag(entry, "Story")) return key;
	if (hasTag(entry, "StoryGroup")) {
		const variantNames = Object.keys(entry.stories);
		if (variantNames.length === 0) return null;
		return `${key}::${variantNames[0]}`;
	}
	return null;
}
/**
* Full-panel overlay shown while stories are being reloaded from disk.
*
* @private
* @returns A rendered reload overlay element.
*/
function ReloadOverlay() {
	return /* @__PURE__ */ jsx(Box, {
		flexDirection: "column",
		flexGrow: 1,
		alignItems: "center",
		justifyContent: "center",
		children: /* @__PURE__ */ jsx(Text, {
			bold: true,
			color: "yellow",
			children: "Reloading stories..."
		})
	});
}
//#endregion
//#region src/stories/viewer/stories-check.tsx
/**
* Non-interactive component that discovers stories, validates them,
* and prints diagnostics using `ctx.status.spinner`, `ctx.log`, and `ctx.report`
* rendered through `<Output />` before exiting.
*
* @param props - The check props.
* @returns A rendered check element.
*/
function StoriesCheck({ include }) {
	const ctx = useScreenContext();
	const { exit } = useApp();
	const started = useRef(false);
	useEffect(() => {
		if (started.current) return;
		started.current = true;
		const [importerError, importer] = createStoryImporter();
		if (importerError) {
			process$1.exitCode = 1;
			ctx.log.error(importerError.message);
			exit();
			return;
		}
		const cwd = process$1.cwd();
		const includePatterns = buildIncludePatterns(include);
		ctx.status.spinner.start("Discovering stories...");
		const run = async () => {
			const result = await discoverStories({
				cwd,
				importer,
				include: includePatterns
			});
			if (result.entries.size === 0) {
				ctx.status.spinner.stop("Discovery complete");
				ctx.log.warn("No stories found.");
				exit();
				return;
			}
			ctx.status.spinner.stop(`Discovered ${String(result.entries.size)} story file${match$1(result.entries.size !== 1).with(true, () => "s").with(false, () => "").exhaustive()}`);
			const checkResult = checkStories(result.entries);
			if ("report" in ctx) {
				checkResult.diagnostics.map((d) => ctx.report.check({
					status: match$1(d.severity).with("error", () => "fail").with("warning", () => "warn").exhaustive(),
					name: d.storyName,
					detail: d.message
				}));
				const errors = checkResult.diagnostics.filter((d) => d.severity === "error");
				const warnings = checkResult.diagnostics.filter((d) => d.severity === "warning");
				ctx.report.summary({
					style: "tally",
					stats: [
						{
							label: "Stories",
							value: String(checkResult.storyCount)
						},
						{
							label: "Errors",
							value: String(errors.length)
						},
						{
							label: "Warnings",
							value: String(warnings.length)
						}
					]
				});
			}
			match$1(checkResult.passed).with(true, () => {
				ctx.log.success("All stories passed validation");
			}).with(false, () => {
				process$1.exitCode = 1;
				ctx.log.error("Story validation failed");
			}).exhaustive();
			exit();
		};
		run().catch((error) => {
			ctx.status.spinner.stop("Discovery failed");
			const message = match$1(error instanceof Error).with(true, () => error.message).with(false, () => "Unknown error during discovery").exhaustive();
			process$1.exitCode = 1;
			ctx.log.error(message);
			exit();
		});
	}, [
		include,
		ctx,
		exit
	]);
	return /* @__PURE__ */ jsx(Output, {});
}
//#endregion
//#region src/stories/viewer/stories-output.tsx
/**
* Non-interactive component that discovers stories, renders the matching
* story (or all stories) to stdout with divider headers, and exits.
*
* When `filter` is empty, all stories are rendered. When `filter` is a
* story name, only that story is rendered.
*
* @param props - The stories output props.
* @returns A rendered stories output element.
*/
function StoriesOutput({ filter, include }) {
	const [state, setState] = useState({ phase: "loading" });
	const { exit } = useApp();
	useEffect(() => {
		const [importerError, importer] = createStoryImporter();
		if (importerError) {
			setState({
				phase: "error",
				message: importerError.message
			});
			return;
		}
		const cwd = process$1.cwd();
		const includePatterns = buildIncludePatterns(include);
		const run = async () => {
			const allStories = collectAllStories((await discoverStories({
				cwd,
				importer,
				include: includePatterns
			})).entries);
			if (allStories.length === 0) {
				setState({
					phase: "error",
					message: "No stories found."
				});
				return;
			}
			const filtered = match$1(filter).with("", () => allStories).otherwise((name) => filterByName(allStories, name));
			if (filtered.length === 0) {
				setState({
					phase: "error",
					message: `Story "${filter}" not found.\nAvailable: ${allStories.map((s) => s.name).join(", ")}`
				});
				return;
			}
			setState({
				phase: "ready",
				stories: filtered
			});
		};
		run().catch((error) => {
			setState({
				phase: "error",
				message: match$1(error instanceof Error).with(true, () => error.message).with(false, () => "Unknown error during discovery").exhaustive()
			});
		});
	}, [filter, include]);
	const shouldExit = state.phase === "error";
	useEffect(() => {
		if (shouldExit) {
			process$1.exitCode = 1;
			exit();
		}
	}, [shouldExit, exit]);
	return match$1(state).with({ phase: "loading" }, () => /* @__PURE__ */ jsx(Text, {
		dimColor: true,
		children: "Discovering stories..."
	})).with({ phase: "error" }, ({ message }) => /* @__PURE__ */ jsx(Text, {
		color: "red",
		children: message
	})).with({ phase: "ready" }, ({ stories }) => /* @__PURE__ */ jsx(StoryRenderer, {
		stories,
		onDone: exit
	})).exhaustive();
}
/**
* Render all resolved stories with divider headers, then exit.
*
* @private
*/
function StoryRenderer({ stories, onDone }) {
	const rendered = useMemo(() => stories.map((s) => {
		const Decorated = applyDecorators(s.component, s.decorators);
		return {
			name: s.name,
			Component: Decorated,
			props: s.props
		};
	}), [stories]);
	useEffect(() => {
		const timer = setTimeout(onDone, 0);
		return () => {
			clearTimeout(timer);
		};
	}, [onDone]);
	return /* @__PURE__ */ jsx(Box, {
		flexDirection: "column",
		children: rendered.map((entry) => /* @__PURE__ */ jsxs(Box, {
			flexDirection: "column",
			children: [/* @__PURE__ */ jsx(StoryDivider, { name: entry.name }), /* @__PURE__ */ jsx(entry.Component, { ...entry.props })]
		}, entry.name))
	});
}
/**
* Render a divider line with the story name.
*
* @private
*/
function StoryDivider({ name }) {
	const label = ` ${name} `;
	return /* @__PURE__ */ jsx(Box, {
		marginBottom: 1,
		children: /* @__PURE__ */ jsxs(Text, {
			dimColor: true,
			children: [
				"────",
				label,
				"─".repeat(Math.max(0, 40 - label.length))
			]
		})
	});
}
/**
* Collect all stories from entries, flattening groups into individual stories.
*
* @private
* @param entries - The discovered story entries.
* @returns A flat array of resolved stories.
*/
function collectAllStories(entries) {
	return [...entries.values()].flatMap((entry) => {
		if (hasTag(entry, "Story")) {
			const s = entry;
			return [{
				name: s.name,
				component: s.component,
				props: s.props,
				decorators: s.decorators
			}];
		}
		if (hasTag(entry, "StoryGroup")) {
			const group = entry;
			return Object.entries(group.stories).map(([variantName, variant]) => ({
				name: `${group.title} / ${variantName}`,
				component: variant.component,
				props: variant.props,
				decorators: [...group.decorators, ...variant.decorators]
			}));
		}
		return [];
	});
}
/**
* Filter stories by name. Supports `Group/Variant` format for exact
* variant selection, or a plain name for partial matching against
* all story names.
*
* @private
* @param stories - All resolved stories.
* @param name - The filter string (e.g. "SystemMonitor/Critical" or "StatusBadge").
* @returns Matching stories.
*/
function filterByName(stories, name) {
	const normalized = name.toLowerCase();
	if (normalized.includes("/")) {
		const compact = normalized.replaceAll(" ", "");
		return stories.filter((s) => s.name.toLowerCase().replaceAll(" ", "") === compact);
	}
	return stories.filter((s) => s.name.toLowerCase().includes(normalized));
}
//#endregion
//#region src/stories/viewer/stories-screen.tsx
/**
* Screen-compatible wrapper that discovers stories on mount, sets up
* the file watcher, and renders the {@link StoriesApp} when ready.
*
* Designed to be used with `screen()` so the framework manages
* stdin/raw mode and the Ink rendering lifecycle.
*
* When `--out` is provided, renders the matching story (or all stories)
* to stdout and exits immediately — useful for piping to LLMs.
*
* @param props - The stories screen props.
* @returns A rendered stories screen element.
*/
function StoriesScreen({ include, out, check }) {
	if (check === true) return /* @__PURE__ */ jsx(StoriesCheck, { include });
	return match$1(out).with(P$1.string, (storyFilter) => /* @__PURE__ */ jsx(StoriesOutput, {
		filter: storyFilter,
		include
	})).with(P$1.nullish, () => /* @__PURE__ */ jsx(StoriesViewer, { include })).exhaustive();
}
/**
* Interactive TUI viewer that discovers stories on mount, sets up
* the file watcher, and renders the {@link StoriesApp} when ready.
*
* @private
* @param props - The viewer props.
* @returns A rendered stories viewer element.
*/
function StoriesViewer({ include }) {
	const [state, setState] = useState({ phase: "loading" });
	const [registry] = useState(createStoryRegistry);
	const { isReloading, onReloadStart, onReloadEnd } = useReloadState();
	useEffect(() => {
		const [importerError, importer] = createStoryImporter();
		if (importerError) {
			setState({
				phase: "error",
				message: importerError.message
			});
			return () => {};
		}
		const cwd = process$1.cwd();
		const includePatterns = buildIncludePatterns(include);
		const run = async () => {
			const result = await discoverStories({
				cwd,
				importer,
				include: includePatterns
			});
			const entries = [...result.entries];
			entries.map(([name, entry]) => registry.set(name, entry));
			if (entries.length === 0) {
				setState({
					phase: "empty",
					errors: result.errors
				});
				return;
			}
			setState({ phase: "ready" });
		};
		run().catch(() => {
			setState({
				phase: "empty",
				errors: []
			});
		});
		const [watchError, watcher] = createStoryWatcher({
			directories: [cwd],
			importer,
			registry,
			onReloadStart,
			onReloadEnd
		});
		if (watchError) return () => {};
		return () => {
			watcher.close();
		};
	}, [
		include,
		registry,
		onReloadStart,
		onReloadEnd
	]);
	return match$1(state).with({ phase: "loading" }, () => /* @__PURE__ */ jsx(Text, { children: "Discovering stories..." })).with({ phase: "error" }, ({ message }) => /* @__PURE__ */ jsx(Text, {
		color: "red",
		children: message
	})).with({ phase: "empty" }, ({ errors }) => /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		children: [/* @__PURE__ */ jsx(Text, { children: "No stories found. Create a .stories.tsx file in your src/ directory to get started." }), errors.length > 0 && /* @__PURE__ */ jsxs(Box, {
			flexDirection: "column",
			marginTop: 1,
			children: [/* @__PURE__ */ jsxs(Text, {
				color: "yellow",
				children: [errors.length, " file(s) failed to import:"]
			}), errors.map((e) => /* @__PURE__ */ jsxs(Text, {
				dimColor: true,
				children: [
					"  ",
					e.filePath,
					": ",
					e.message
				]
			}, e.filePath))]
		})]
	})).with({ phase: "ready" }, () => /* @__PURE__ */ jsx(StoriesApp, {
		registry,
		isReloading
	})).exhaustive();
}
//#endregion
export { MAX_EDITABLE_FIELDS, STORY_FILE_SUFFIXES, StoriesScreen, checkStories, createStoryImporter, createStoryRegistry, createStoryWatcher, discoverStories, resolveControlKind, schemaToFieldDescriptors, stories, story, validateProps, withContext, withFullScreen, withLayout };

//# sourceMappingURL=index.js.map